/**
 * Format a number as Brazilian Real currency
 * @param {number|string} value
 * @returns {string} formatted currency string
 */
export const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
};

/**
 * Format a date string (YYYY-MM-DD or ISO) as DD/MM/YYYY
 * @param {string} dateStr
 * @returns {string}
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    // Handle ISO strings and plain date strings
    const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return new Intl.DateTimeFormat('pt-BR').format(date);
  } catch {
    return dateStr;
  }
};

/**
 * Format a datetime string as DD/MM/YYYY HH:mm
 * @param {string} dateStr
 * @returns {string}
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return dateStr;
  }
};

/**
 * Format a duration string (HH:MM:SS or ISO 8601 duration) as HH:mm
 * @param {string} duration
 * @returns {string}
 */
export const formatDuration = (duration) => {
  if (!duration) return '00:00';
  try {
    // Handle HH:MM:SS or H:M:S
    const parts = duration.split(':');
    const h = parseInt(parts[0] || '0', 10);
    const m = parseInt(parts[1] || '0', 10);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } catch {
    return '00:00';
  }
};

/**
 * Format a time string as HH:mm
 * @param {string} timeStr
 * @returns {string}
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return '--:--';
  try {
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return timeStr.slice(0, 5);
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return timeStr;
  }
};

/**
 * Parse a duration string "HH:mm" to decimal hours
 * @param {string} duration
 * @returns {number}
 */
export const durationToDecimal = (duration) => {
  if (!duration) return 0;
  const [h, m] = duration.split(':').map(Number);
  return h + (m || 0) / 60;
};

/**
 * Convert decimal hours to "HH:mm"
 * @param {number} value
 * @returns {string}
 */
export const decimalToDuration = (value) => {
  if (!value && value !== 0) return '00:00';
  const h = Math.floor(value);
  const m = Math.round((value - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
